import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Grid,
    Stack,
    Box,
    Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import {
    Input,
    Select,
    CommonModal,
} from '../Common/index';
import { trakrefBlue } from '../../color_utils';
import { trakrefFontFamilyBold } from '../../utils';
import { GethAssetIdentityTypes } from '../../CodesCache';
import Routes from '../../routes';

const initialFieldValue = {
    value: null,
    hasError: false
}

const AliasFormModal = ({
    open,
    closeModal,
    alias,
    assetId,
    onSaveSuccess,
    onDeleteSuccess
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [valueName, setValueName] = useState(initialFieldValue);
    const [typeId, setTypeId] = useState(initialFieldValue);
    const [options, setOptions] = useState([]);
    const headerName = !alias ? "Add Alias / External ID" : "Edit Alias / External ID";
    const buttonTitle = !alias ? "Add Alias" : "Edit Alias";

    const fetchAssetIdentityType = useCallback(async () => {
        const newOptions = await GethAssetIdentityTypes();
        setOptions(newOptions);
    }, [assetId]);

    const onModalClose = () => {
        closeModal();
        setValueName(initialFieldValue);
        setTypeId(initialFieldValue);
    }

    const onSave = async () => {
        try {
            setIsLoading(true);

            let isFormValid = true;

            if (!valueName.value) {
                setValueName({ ...valueName, hasError: true })
            }

            if (!typeId.value) {
                setTypeId({ ...typeId, hasError: true })
            }

            if (!isFormValid) {
                setIsLoading(false);
                return;
            }

            const body = {
                assetId,
                typeId: typeId.value,
                id: !alias ? null : alias.id,
                value: valueName.value,
            }

            const endpoint = !alias ? Routes.AssetIdentity.Create() : Routes.AssetIdentity.Update();

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(body)
            });
            const data = await response.json();

            if (response.status === 200) {
                onModalClose();
                onSaveSuccess(data);
            }
        }
        catch {
            setIsLoading(false);
        }
        finally {
            setIsLoading(false);
        }
    }

    const onDelete = async () => {
        try {
            setIsDeleteLoading(true);
            const response = await fetch(Routes.AssetIdentity.Delete(alias.id), {
                method: 'POST',
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });
            const data = await response.json();

            if (response.status === 200) {
                onModalClose();
                onDeleteSuccess(alias.id);
            }
        }
        catch {
            console.error("something went wrong!");
            setIsDeleteLoading(false);
        }
        finally {
            setIsDeleteLoading(false);
        }
    }

    useEffect(() => {
        fetchAssetIdentityType();
    }, [fetchAssetIdentityType])

    useEffect(() => {
        if (!alias) {
            return;
        }

        setValueName({
            ...valueName,
            value: alias.identityValue
        });

        setTypeId({
            ...typeId,
            value: alias.assetIdentityTypeId
        });
    }, [alias])

    return (
        <CommonModal
            open={open}
            close={onModalClose}
            header={headerName}
            bottom={24}
            scroll="paper"
        >
            <Grid container pl={10} pr={10}>
                <Grid item xs={12} lg={12} mb={2} pt={2} align="center">
                    <Stack spacing={3}>
                        <Input
                            required
                            fullWidth
                            variant={"standard"}
                            label={"Alias / External ID"}
                            value={valueName.value}
                            onChange={(e) => setValueName({
                                hasError: false,
                                value: e.target.value
                            })}
                            error={valueName.hasError}
                            maxCharacterLength={150}
                        />
                        <Select
                            required
                            fullWidth
                            variant={"standard"}
                            label="Alias Type"
                            options={options}
                            value={typeId.value}
                            onChange={(e) => setTypeId({
                                hasError: false,
                                value: e.target.value
                            })}
                            error={typeId.hasError}
                        />
                    </Stack>
                </Grid>
                <Grid item xs={12} lg={12} align="center">
                    <Box mt={2} mb={2} sx={{ width: "100%" }}>
                        <LoadingButton
                            fullWidth
                            size="large"
                            variant="contained"
                            loading={isLoading}
                            onClick={onSave}
                            sx={{
                                "& .MuiCircularProgress-root": {
                                    color: "#FFFFFF",
                                    width: "21px !important",
                                    height: "21px !important",
                                },
                            }}
                            style={{
                                backgroundColor: trakrefBlue,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: trakrefFontFamilyBold,
                                    color: isLoading ? "transparent" : "#fff",
                                    backgroundColor: trakrefBlue,
                                    letterSpacing: "1.2px",
                                    padding: "1% 0%",
                                    fontSize: 17,
                                    fontWeight: 400,
                                }}
                            >
                                {buttonTitle}
                            </Typography>
                        </LoadingButton>
                        {
                            alias && <LoadingButton
                                fullWidth
                                size="large"
                                variant="contained"
                                loading={isDeleteLoading}
                                onClick={onDelete}
                                sx={{
                                    "& .MuiCircularProgress-root": {
                                        color: trakrefBlue,
                                        width: "21px !important",
                                        height: "21px !important",
                                    },
                                    ":hover": {
                                        boxShadow: "none"
                                    },
                                    marginTop: "15px",
                                    boxShadow: "none"
                                }}
                                style={{
                                    backgroundColor: "transparent",
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontFamily: trakrefFontFamilyBold,
                                        color: isDeleteLoading ? 'transparent' : trakrefBlue,
                                        letterSpacing: "1.2px",
                                        padding: "1% 0%",
                                        fontSize: 17,
                                        fontWeight: 400,
                                    }}
                                >
                                    DELETE
                                </Typography>
                            </LoadingButton>
                        }
                    </Box>
                </Grid>
            </Grid>
        </CommonModal>
    );
};

AliasFormModal.propTypes = {
    open: PropTypes.bool.isRequired,
    closeModal: PropTypes.func.isRequired,
    assetId: PropTypes.number.isRequired,
    alias: PropTypes.object,
    onSaveSuccess: PropTypes.func.isRequired
};

export default AliasFormModal;
